export type ErrorType =
  | 'bad_request'
  | 'conflict'
  | 'unauthorized'
  | 'forbidden'
  | 'entitlement_required'
  | 'not_found'
  | 'rate_limit'
  | 'plan_limit'
  | 'insufficient_credits'
  | 'payload_too_large'
  | 'unsupported_media_type'
  | 'organization_locked'
  | 'workspace_read_only'
  | 'workspace_access_revoked'
  | 'offline'
  | 'message_too_long'
  | 'internal_error';

export type Surface =
  | 'chat'
  | 'auth'
  | 'api'
  | 'stream'
  | 'database'
  | 'history'
  | 'vote'
  | 'document'
  | 'suggestions';

export type ErrorCode = `${ErrorType}:${Surface}`;

export type ErrorVisibility = 'response' | 'log' | 'none';

/**
 * Closed set of safe, client-localizable specificity tokens. Each maps to an
 * `errors.detail.*` translation key. Never put free-form English here — that
 * would re-leak internal detail and bypass i18n. Grow this union deliberately.
 */
export type DetailKey =
  | 'file_too_large'
  | 'file_type_unsupported'
  | 'legacy_xls_unsupported'
  | 'ingestion_recovery_uncertain'
  | 'member_removal_forbidden'
  | 'cannot_remove_sole_member'
  | 'project_ownership_transfer_required'
  | 'duplicate_bucket_name'
  | 'tool_execution_failed'
  | 'wrong_account'
  | 'domain_not_allowed'
  | 'domain_claimed_by_other_org'
  | 'account_not_connected'
  | 'export_too_large'
  | 'regulatory_location_required'
  | 'regulatory_location_unavailable'
  | 'regulatory_overlay_unavailable'
  | 'regulatory_rule_changed'
  | 'regulatory_rule_history'
  | 'regulatory_manual_additions'
  | 'regulatory_reconcile_import'
  | 'document_group_name_taken'
  | 'stale_page'
  | 'code_package_invalid_bundle'
  | 'code_package_changed'
  | 'code_package_unknown_version'
  | 'code_package_has_file'
  | 'code_package_wrong_path'
  | 'code_package_file_mismatch'
  | 'code_package_failed'
  | 'folder_managed_by_sync'
  | 'sync_root_folder_taken'
  | 'sync_item_already_in_destination'
  | 'sync_item_not_in_root'
  | 'sync_root_overlaps'
  | 'sync_provider_not_available'
  | 'sync_invalid_root_folder'
  | 'sync_connection_not_active'
  | 'sync_root_not_approvable'
  | 'sync_microsoft_work_account_required'
  | 'sync_connection_tenant_mismatch'
  | 'sync_source_paused';

export interface ChatSDKErrorOptions {
  /** Underlying error/value. Kept on the native cause-chain for logs; never serialized to the client. */
  cause?: unknown;
  /** Safe, localizable specificity token surfaced to the client via `toResponse()`. */
  detail?: DetailKey;
}

export const visibilityBySurface: Record<Surface, ErrorVisibility> = {
  database: 'log',
  chat: 'response',
  auth: 'response',
  stream: 'response',
  api: 'response',
  history: 'response',
  vote: 'response',
  document: 'response',
  suggestions: 'response',
};

export class ChatSDKError extends Error {
  public type: ErrorType;
  public surface: Surface;
  public statusCode: number;
  public detail?: DetailKey;

  constructor(errorCode: ErrorCode, cause?: string);
  constructor(errorCode: ErrorCode, options?: ChatSDKErrorOptions);
  constructor(
    errorCode: ErrorCode,
    optionsOrCause?: string | ChatSDKErrorOptions,
  ) {
    const [type, surface] = errorCode.split(':');
    const message = getMessageByErrorCode(errorCode);

    // Back-compat: a positional string is treated as the cause.
    const options: ChatSDKErrorOptions =
      typeof optionsOrCause === 'string'
        ? { cause: optionsOrCause }
        : (optionsOrCause ?? {});

    // Preserve the real error on the native cause-chain (stack-friendly).
    super(
      message,
      options.cause !== undefined ? { cause: options.cause } : undefined,
    );

    this.type = type as ErrorType;
    this.surface = surface as Surface;
    this.detail = options.detail;
    // A failed query is our outage whatever type the call site picked, so it
    // is a 5xx: clients retry it and nobody mistakes it for bad input.
    this.statusCode =
      this.surface === 'database' ? 500 : getStatusCodeByType(this.type);
  }

  /**
   * Pure, side-effect-free serializer. Returns `{ code, message, detail? }` and
   * NEVER `cause` (the `database` carve-out also masks `code`). Logging and
   * error capture live in the route funnel (Phase 2), not here, so this stays
   * isomorphic and safe in client bundles.
   */
  public toResponse() {
    const code: ErrorCode = `${this.type}:${this.surface}`;
    const visibility = visibilityBySurface[this.surface];

    const { message, detail, statusCode } = this;

    if (visibility === 'log') {
      // Interim: the log-only (database) surface is the one path that logged in
      // toResponse() pre-Phase-1, and several routes return it directly with no
      // other logging. Keep this isomorphic, zero-import console.error until the
      // Phase 2 route funnel owns logging, so masked DB failures stay debuggable.
      console.error({ code, message, cause: this.cause });

      // Mask database failures: never expose the real code/detail to the client.
      return Response.json(
        { code: '', message: 'Something went wrong. Please try again later.' },
        { status: statusCode },
      );
    }

    return Response.json(
      detail ? { code, message, detail } : { code, message },
      { status: statusCode },
    );
  }
}

export function getMessageByErrorCode(errorCode: ErrorCode): string {
  if (errorCode.includes('database')) {
    return 'An error occurred while executing a database query.';
  }

  switch (errorCode) {
    case 'bad_request:api':
      return "The request couldn't be processed. Please check your input and try again.";

    case 'conflict:api':
      return 'This conflicts with a recent change. Refresh the page and try again.';

    case 'message_too_long:chat':
      return 'Your message is too long. Please send a shorter version.';

    case 'unauthorized:auth':
      return 'You need to sign in before continuing.';
    case 'forbidden:auth':
      return "You don't have access to this in your organization. Contact an admin to request access.";
    case 'entitlement_required:api':
      return 'This feature is not included in your current plan.';

    case 'rate_limit:chat':
      return 'You have exceeded your maximum number of messages for the day. Please try again later.';
    case 'plan_limit:chat':
      return 'Your organization has reached its plan limit.';
    case 'insufficient_credits:chat':
      return 'No credits remain in the current billing period.';
    case 'payload_too_large:api':
      return 'The export is too large. Please reduce your selection and try again.';
    case 'not_found:chat':
      return 'The requested chat was not found. Please check the chat ID and try again.';
    case 'forbidden:chat':
      return 'This chat belongs to another user. Please check the chat ID and try again.';
    case 'unauthorized:chat':
      return 'You need to sign in to view this chat. Please sign in and try again.';
    case 'offline:chat':
      return "We're having trouble sending your message. Please check your internet connection and try again.";

    case 'not_found:document':
      return 'The requested document was not found. Please check the document ID and try again.';
    case 'forbidden:document':
      return 'This document belongs to another user. Please check the document ID and try again.';
    case 'unauthorized:document':
      return 'You need to sign in to view this document. Please sign in and try again.';
    case 'bad_request:document':
      return 'The request to create or update the document was invalid. Please check your input and try again.';

    case 'organization_locked:chat':
      return "Your organization's trial is over. Book a meeting to continue using this feature.";
    case 'organization_locked:api':
      return "Your organization's trial is over. Book a meeting to continue using this feature.";
    case 'workspace_read_only:api':
      return 'This workspace is read-only. Update billing or contact a workspace owner to make changes.';
    case 'workspace_access_revoked:api':
      return 'You no longer have access to this workspace.';

    default:
      return 'Something went wrong. Please try again later.';
  }
}

function getStatusCodeByType(type: ErrorType) {
  switch (type) {
    case 'bad_request':
    case 'message_too_long':
      return 400;
    case 'conflict':
      return 409;
    case 'unauthorized':
      return 401;
    case 'forbidden':
    case 'entitlement_required':
    case 'organization_locked':
    case 'workspace_read_only':
    case 'workspace_access_revoked':
      return 403;
    case 'not_found':
      return 404;
    case 'payload_too_large':
      return 413;
    case 'unsupported_media_type':
      return 415;
    case 'insufficient_credits':
      return 402;
    case 'rate_limit':
    case 'plan_limit':
      return 429;
    case 'offline':
      return 503;
    case 'internal_error':
      return 500;
    default:
      return 500;
  }
}
